import { useState, useCallback, useRef } from 'react';
import { Upload, FileText, Image as ImageIcon, FileType, Camera, X, Loader2, AlertTriangle } from 'lucide-react';

interface FileUploaderProps {
    onFileReady: (fileBase64: string, mimeType: string, fileName: string) => void;
    isProcessing: boolean;
}

const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const PDF_TYPE = 'application/pdf';
const DOCX_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DOC_LEGACY_TYPE = 'application/msword';

const ACCEPT_ATTR = [
    ...ACCEPTED_IMAGE_TYPES,
    PDF_TYPE,
    DOCX_TYPE,
    // File extensions as a fallback for browsers that don't recognise the MIME types
    '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.pdf', '.docx',
].join(',');

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_DOC_BYTES = 20 * 1024 * 1024;

function resolveKind(file: File): 'image' | 'pdf' | 'docx' | 'doc' | 'unknown' {
    if (ACCEPTED_IMAGE_TYPES.includes(file.type)) return 'image';
    if (file.type === PDF_TYPE) return 'pdf';
    if (file.type === DOCX_TYPE) return 'docx';
    if (file.type === DOC_LEGACY_TYPE) return 'doc';

    // Fallback by extension when the browser reports empty/odd MIME
    const name = file.name.toLowerCase();
    if (name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png') || name.endsWith('.webp') || name.endsWith('.heic') || name.endsWith('.heif')) return 'image';
    if (name.endsWith('.pdf')) return 'pdf';
    if (name.endsWith('.docx')) return 'docx';
    if (name.endsWith('.doc')) return 'doc';
    return 'unknown';
}

function resolveMimeType(file: File): string {
    if (file.type) return file.type;
    const name = file.name.toLowerCase();
    if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
    if (name.endsWith('.png')) return 'image/png';
    if (name.endsWith('.webp')) return 'image/webp';
    if (name.endsWith('.heic')) return 'image/heic';
    if (name.endsWith('.heif')) return 'image/heif';
    if (name.endsWith('.pdf')) return PDF_TYPE;
    if (name.endsWith('.docx')) return DOCX_TYPE;
    return '';
}

export function FileUploader({ onFileReady, isProcessing }: FileUploaderProps) {
    const [dragOver, setDragOver] = useState(false);
    const [selectedFile, setSelectedFile] = useState<{ name: string; kind: 'image' | 'pdf' | 'docx' } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);

    const processFile = useCallback((file: File) => {
        setError(null);
        const kind = resolveKind(file);

        if (kind === 'doc') {
            setError('Legacy .doc files are not supported. Please open the file in Word and use "Save As" to save it as .docx.');
            return;
        }
        if (kind === 'unknown') {
            setError(`Unsupported file type. Please upload an image (JPEG/PNG/WEBP/HEIC), a PDF, or a Word (.docx) document.`);
            return;
        }

        const sizeLimit = kind === 'image' ? MAX_IMAGE_BYTES : MAX_DOC_BYTES;
        if (file.size > sizeLimit) {
            const limitMb = Math.round(sizeLimit / (1024 * 1024));
            setError(`File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum for ${kind} is ${limitMb} MB.`);
            return;
        }

        const mimeType = resolveMimeType(file);
        setSelectedFile({ name: file.name, kind });

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result as string;
            const base64 = dataUrl.split(',')[1] ?? '';
            onFileReady(base64, mimeType, file.name);
        };
        reader.onerror = () => {
            setError('Failed to read the file. Please try again.');
            setSelectedFile(null);
        };
        reader.readAsDataURL(file);
    }, [onFileReady]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) processFile(file);
    }, [processFile]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => setDragOver(false), []);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) processFile(file);
    }, [processFile]);

    const handleClear = useCallback(() => {
        setSelectedFile(null);
        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (cameraInputRef.current) cameraInputRef.current.value = '';
    }, []);

    const kindIcon = selectedFile?.kind === 'image'
        ? <ImageIcon className="w-5 h-5 text-blue-600 flex-shrink-0" />
        : selectedFile?.kind === 'pdf'
            ? <FileText className="w-5 h-5 text-red-600 flex-shrink-0" />
            : <FileType className="w-5 h-5 text-blue-700 flex-shrink-0" />;

    return (
        <div className="space-y-3">
            {error && (
                <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {!selectedFile ? (
                <>
                    <div
                        className={`border-2 border-dashed rounded-xl p-6 sm:p-8 text-center cursor-pointer transition-colors ${
                            dragOver
                                ? 'border-blue-400 bg-blue-50'
                                : 'border-gray-300 hover:border-blue-300 hover:bg-gray-50'
                        }`}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                        <p className="text-sm font-medium text-gray-700">
                            Drop a file here or tap to select
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            Photo of handwritten notes, PDF, or Word (.docx) — up to 20 MB
                        </p>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPT_ATTR}
                            className="hidden"
                            onChange={handleFileSelect}
                        />
                    </div>

                    {/* Camera button — prominent on mobile, hidden on desktop (no camera to target) */}
                    <button
                        type="button"
                        onClick={() => cameraInputRef.current?.click()}
                        className="sm:hidden w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-700 text-sm font-medium hover:border-gray-300"
                    >
                        <Camera className="w-5 h-5" /> Take a photo
                    </button>
                    <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleFileSelect}
                    />
                </>
            ) : (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border">
                    {kindIcon}
                    <span className="text-sm font-medium text-gray-700 truncate flex-1">
                        {selectedFile.name}
                    </span>
                    {isProcessing ? (
                        <div className="flex items-center gap-2 text-sm text-blue-600">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Extracting…</span>
                        </div>
                    ) : (
                        <button
                            onClick={handleClear}
                            className="p-1 hover:bg-gray-200 rounded"
                            title="Remove file"
                        >
                            <X className="w-4 h-4 text-gray-500" />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
